import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAbility } from '@casl/react';
import { Edit, MapPin, Plus, Power, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Can } from '../../components/Can';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Branch, BranchContact, ListStatusFilter } from '../../types/lists.types';
import { branchesApi } from '../../utils/lists-api';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { orDash } from './utils/lists-utils';
import StatusPill from './components/StatusPill';
import BranchFormDialog from './components/BranchFormDialog';

export default function BranchesPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.LIST_BRANCH);

    const [branches, setBranches] = useState<Branch[]>([]);
    const [users, setUsers] = useState<BranchContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<ListStatusFilter>('active');
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Branch | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setBranches(await branchesApi.list({ status }));
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load branches');
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => {
        if (canRead) fetchData();
    }, [canRead, fetchData]);

    // Contact options are only needed by the dialog, so fetch once rather than per refresh.
    // The users API wraps its payload as { success, users }, and all_users=true returns the
    // lightweight active-user select without the per-user permission expansion.
    useEffect(() => {
        if (!canRead) return;
        api.get<{ success: boolean; users: BranchContact[] }>('/api/users?all_users=true')
            .then((res) => setUsers(Array.isArray(res?.users) ? res.users : []))
            .catch(() => toast.error('Failed to load contact list'));
    }, [canRead]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return branches;
        return branches.filter((b) =>
            [b.name, b.address, b.mainContactPerson?.name]
                .filter(Boolean)
                .some((v) => (v as string).toLowerCase().includes(q))
        );
    }, [branches, search]);

    const handleToggleStatus = async (branch: Branch) => {
        try {
            await branchesApi.setStatus(branch.id, !branch.isActive);
            toast.success(`${branch.name} set to ${branch.isActive ? 'Inactive' : 'Active'}`);
            fetchData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update status');
        }
    };

    const openAdd = () => { setEditing(null); setDialogOpen(true); };
    const openEdit = (branch: Branch) => { setEditing(branch); setDialogOpen(true); };

    if (!canRead) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Branches</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Used by the Project List, where only Active branches are selectable.
                    </p>
                </div>
                <Can I={ACTIONS.CREATE} a={RESOURCES.LIST_BRANCH}>
                    <Button
                        onClick={openAdd}
                        className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        <Plus size={16} /><span>Add Branch</span>
                    </Button>
                </Can>
            </div>

            {/* Active / Inactive tabs */}
            <Tabs value={status} onValueChange={(v) => setStatus(v as ListStatusFilter)} className="w-full">
                <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
                    <TabsTrigger value="active" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Active
                    </TabsTrigger>
                    <TabsTrigger value="inactive" className="rounded-full flex-1 md:flex-none px-4 py-2 data-[state=active]:bg-white">
                        Inactive
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {/* Search */}
            <div className="flex flex-col md:flex-row items-start md:items-center w-full gap-4 flex-wrap">
                <div className="relative w-full md:flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search branches..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10 w-full"
                    />
                </div>
                <span className="text-sm text-muted-foreground">{filtered.length} records</span>
            </div>

            {loading ? (
                <div className="space-y-3 animate-pulse">
                    {[0, 1, 2].map((i) => <div key={i} className="h-14 bg-gray-300 rounded-md" />)}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No branches found</div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-3">
                        {filtered.map((b) => (
                            <Card key={b.id} className="p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <MapPin size={16} className="text-muted-foreground shrink-0" />
                                        <span className="font-medium truncate">{b.name}</span>
                                    </div>
                                    <StatusPill isActive={b.isActive} />
                                </div>
                                <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                                    <p>{b.address}</p>
                                    <p className="flex items-center gap-1.5">
                                        <User size={14} />
                                        {b.mainContactPerson?.name ?? '–'}
                                    </p>
                                </div>
                                <div className="flex justify-end gap-2 mt-3">
                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_BRANCH}>
                                        <Button variant="outline" size="icon" onClick={() => openEdit(b)} aria-label="Edit">
                                            <Edit size={16} />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => handleToggleStatus(b)}
                                            aria-label={b.isActive ? 'Set inactive' : 'Set active'}
                                            className={cn(b.isActive && 'text-destructive')}
                                        >
                                            <Power size={16} />
                                        </Button>
                                    </Can>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* Desktop table */}
                    <Card className="hidden md:block">
                        <CardContent className="p-3">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="font-semibold text-slate-900">Branch Name</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Address</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Main Contact</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Description</TableHead>
                                        <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((b) => (
                                        <TableRow key={b.id}>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <MapPin size={16} className="text-muted-foreground" />
                                                    {b.name}
                                                </div>
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[260px]">{b.address}</TableCell>
                                            <TableCell>
                                                {b.mainContactPerson ? (
                                                    <div className="flex items-center gap-2">
                                                        <User size={16} className="text-muted-foreground" />
                                                        {b.mainContactPerson.name}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted-foreground">–</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="whitespace-normal max-w-[260px] text-muted-foreground">
                                                {orDash(b.description)}
                                            </TableCell>
                                            <TableCell><StatusPill isActive={b.isActive} /></TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-2">
                                                    <Can I={ACTIONS.UPDATE} a={RESOURCES.LIST_BRANCH}>
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(b)} aria-label="Edit">
                                                            <Edit size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => handleToggleStatus(b)}
                                                            aria-label={b.isActive ? 'Set inactive' : 'Set active'}
                                                            className={cn(b.isActive && 'text-destructive')}
                                                        >
                                                            <Power size={16} />
                                                        </Button>
                                                    </Can>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}

            <BranchFormDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                branch={editing}
                users={users}
                onSaved={fetchData}
            />
        </div>
    );
}
